import React, { useCallback, useState, useRef } from "react";

interface FileUploadZoneProps {
  onFile: (file: File) => void;
  uploading: boolean;
}

const ACCEPT = ".pdf,.docx,.txt";
const ALLOWED_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "text/plain",
]);
const ALLOWED_EXTS = new Set([".pdf", ".docx", ".txt"]);

function isAllowed(file: File): boolean {
  if (ALLOWED_TYPES.has(file.type)) return true;
  const ext = "." + file.name.split(".").pop()?.toLowerCase();
  return ALLOWED_EXTS.has(ext);
}

const FileUploadZone: React.FC<FileUploadZoneProps> = ({ onFile, uploading }) => {
  const [dragging, setDragging] = useState(false);
  const [typeError, setTypeError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (!isAllowed(file)) {
      setTypeError("Only PDF, DOCX, and TXT files are supported.");
      return;
    }
    setTypeError("");
    onFile(file);
  }, [onFile]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    handleFile(e.dataTransfer.files[0]);
  }, [handleFile]);

  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4">
      <div className="w-full max-w-xl">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4"
            style={{ background: "linear-gradient(135deg,#7C3AED,#2563EB)" }}>
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Chat with your Document</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Upload a PDF, Word file, or text file — then ask anything about it
          </p>
        </div>

        {/* Drop zone */}
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={onDrop}
          onClick={() => !uploading && inputRef.current?.click()}
          className={`relative border-2 border-dashed rounded-2xl p-10 text-center cursor-pointer
            transition-all duration-200
            ${dragging
              ? "border-purple-500 bg-purple-50"
              : "border-gray-300 bg-white hover:border-purple-400 hover:bg-gray-50"
            }
            ${uploading ? "pointer-events-none opacity-60" : ""}
          `}
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            className="hidden"
            onChange={e => handleFile(e.target.files?.[0])}
            disabled={uploading}
          />

          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <svg className="w-10 h-10 text-purple-500 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z"/>
              </svg>
              <p className="text-sm font-medium text-purple-700">Parsing document…</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                  d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <div>
                <p className="text-base font-semibold text-gray-700">
                  {dragging ? "Drop it here" : "Drag & drop your file"}
                </p>
                <p className="text-sm text-gray-400 mt-0.5">or click to browse</p>
              </div>
              <div className="flex items-center gap-2 mt-1">
                {["PDF", "DOCX", "TXT"].map(ext => (
                  <span key={ext}
                    className="px-2.5 py-0.5 text-xs font-semibold bg-gray-100 text-gray-600 rounded-full">
                    {ext}
                  </span>
                ))}
                <span className="text-xs text-gray-400">· max 15 MB</span>
              </div>
            </div>
          )}
        </div>

        {typeError && (
          <p className="mt-3 text-sm text-red-600 text-center">{typeError}</p>
        )}

        {/* Hint cards */}
        <div className="grid grid-cols-3 gap-3 mt-6">
          {[
            { icon: "📋", text: "Circulars & Orders" },
            { icon: "📊", text: "Reports & Data" },
            { icon: "📚", text: "Policy Documents" },
          ].map(({ icon, text }) => (
            <div key={text}
              className="text-center text-xs text-gray-500 bg-white border border-gray-100
                rounded-xl p-3 shadow-sm">
              <div className="text-xl mb-1">{icon}</div>
              {text}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default FileUploadZone;
